import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import fetcher from "../utils/fetcher";
import mediaUrl from "../utils/mediaUrl";
import { general2DProjection, invert3x3, applyPoint, quadMatrix3d } from "../utils/quadTransform";
import { addToCart } from "../redux/cartSlice";
import { addProduct, removeProduct } from "../redux/wishSlice";
import ProductImage from "../components/ProductImage";
import { ImSpinner2 } from "react-icons/im";
import {
  FiImage, FiCheck, FiMaximize2, FiMinimize2, FiCamera, FiLayout, FiHeart, FiChevronDown,
  FiSliders, FiShoppingCart, FiCrop, FiTool,
} from "react-icons/fi";
import { BsLightningChargeFill } from "react-icons/bs";
import { RiRulerLine } from "react-icons/ri";
import { FaRegTrashAlt, FaCouch } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import styles from "../styles/wallPreview.module.css";

// Photo tips shown in the wall step
const WALL_TIPS = [
  { icon: <FiLayout />, text: "حدّد الحائط بدقة" },
  { icon: <FiCamera />, text: "صوّر بشكل مستقيم" },
  { icon: <FaCouch />, text: "أظهر جزءًا من الأثاث" },
  { icon: <FiMaximize2 />, text: "أظهر الحائط كاملاً" },
];

// Picking the design and placing it on the wall happen in one step (PLACE)
const STEPS = { WALL: 1, OUTLINE: 2, PLACE: 3, CONFIRM: 4 };
const GALLERY_LIMIT = 12;
const MIN_OVERLAY_WIDTH_PCT = 0.05;
const INITIAL_OVERLAY_WIDTH_PCT = 0.3;
// The "imagine a printing here" preview shown in the Outline step: a generic centered
// box, not tied to any real product — just to convey scale before picking one.
const PLACEHOLDER_WIDTH_PCT = 0.4;
const PLACEHOLDER_ASPECT = 1;
// A slight inset from the photo's edges so the outline handles start out grabbable
// instead of sitting flush against the image border.
const DEFAULT_WALL_CORNERS = {
  tl: { x: 0.08, y: 0.08 },
  tr: { x: 0.92, y: 0.08 },
  bl: { x: 0.08, y: 0.92 },
  br: { x: 0.92, y: 0.92 },
};

// Builds the projective transform mapping the wall's own flat, undistorted plane (a
// unit square) onto its photographed, possibly-tilted outline at a given render size —
// plus the outline's average pixel width/height, a stand-in for its real-world aspect
// ratio (only the wall's real width, in meters, is ever entered by the user).
function useQuadGeometry(corners, size) {
  return useMemo(() => {
    if (!size.width || !size.height) return { homography: null, pxSize: { width: 0, height: 0 } };
    const pt = (p) => [p.x * size.width, p.y * size.height];
    const tl = pt(corners.tl);
    const tr = pt(corners.tr);
    const bl = pt(corners.bl);
    const br = pt(corners.br);
    const homography = general2DProjection(0, 0, 1, 0, 0, 1, 1, 1, ...tl, ...tr, ...bl, ...br);
    const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
    const pxSize = {
      width: (dist(tl, tr) + dist(bl, br)) / 2,
      height: (dist(tl, bl) + dist(tr, br)) / 2,
    };
    return { homography, pxSize };
  }, [corners, size]);
}

const WallPreview = () => {
  const [searchParams] = useSearchParams();
  const preselectedId = searchParams.get("product");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const cartItems = useSelector((state) => state.cart.items);

  const wishlist = useSelector((state) => state.wishlist.wishlist);

  const [step, setStep] = useState(STEPS.WALL);
  // Design strip filters in the Place step
  const [galleryTab, setGalleryTab] = useState("all"); // "all" | "wishlist"
  const [galleryCategory, setGalleryCategory] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  const wallInputRef = useRef(null);
  const [wallFile, setWallFile] = useState(null);
  const [wallPreview, setWallPreview] = useState(null);
  const [wallWidth, setWallWidth] = useState("");

  // The wall's outline within the photo, as fractions (0-1) of the image — lets the
  // user trace a wall that's photographed at an angle so the artwork can be tilted
  // (via a perspective transform) to match it, instead of sitting flat on top.
  const [wallCorners, setWallCorners] = useState(DEFAULT_WALL_CORNERS);
  const outlineImgRef = useRef(null);
  const [outlineImgSize, setOutlineImgSize] = useState({ width: 0, height: 0 });

  // selectedProduct: { productId, name, image_url (AR cutout, for overlay), catalogImage
  // (main product photo, for cart/order records), dimensions (catalog sizes) }
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProduct, setLoadingProduct] = useState(false);

  // "preset" = pick one of the product's real catalog sizes, price shown instantly and
  // ordered through the normal priced checkout. "custom" = freely resize, submitted as
  // a quote request since an arbitrary size can't be safely auto-priced.
  const [sizingMode, setSizingMode] = useState("preset");
  const [selectedDim, setSelectedDim] = useState(null);

  const wallImgRef = useRef(null);
  const [wallImgSize, setWallImgSize] = useState({ width: 0, height: 0 });
  const [naturalAspect, setNaturalAspect] = useState(1);
  // Position/size are stored as fractions of the rendered wall image box, not raw
  // pixels — that way the placement stays correct when the image is resized (window
  // resize, or toggling the maximized view) instead of drifting.
  const [overlay, setOverlay] = useState({ leftPct: 0, topPct: 0, widthPct: 0 });
  const [interacting, setInteracting] = useState(false);
  const [maximized, setMaximized] = useState(false);

  // Phones: the Place step is laid out to fit one screen, so the wall photo must
  // *cover* the scene box. object-fit would break the overlay geometry (corners are
  // fractions of the photo), so instead the photo element itself is sized to cover
  // the scene and the scene clips what overflows.
  const sceneRef = useRef(null);
  const studioRef = useRef(null);
  const [sceneSize, setSceneSize] = useState({ width: 0, height: 0 });
  const [wallNatural, setWallNatural] = useState({ width: 0, height: 0 });
  const [isPhone, setIsPhone] = useState(() => window.matchMedia("(max-width: 711px)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 711px)");
    const onChange = () => setIsPhone(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const [material, setMaterial] = useState("matte");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setName((prev) => prev || user?.name || "");
    setMobile((prev) => prev || user?.mobile || "");
  }, [user]);

  const fetchProductById = useCallback(async (productId) => {
    setLoadingProduct(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`);
      const p = res.data?.products?.[0];
      if (!p) throw new Error("not found");
      const mainImage = p.images?.find((img) => img.is_main) ?? p.images?.[0];
      const dimensions = p.dimensions || [];

      setOverlay({ leftPct: 0, topPct: 0, widthPct: 0 });
      setSelectedDim(null);
      setSizingMode(dimensions.length ? "preset" : "custom");
      setSelectedProduct({
        productId: Number(productId),
        name: p.name,
        image_url: p.arImage ?? mainImage?.image_url,
        catalogImage: mainImage?.image_url,
        dimensions,
      });
    } catch (error) {
      toast.error(<span className="toast">لم نتمكن من تحميل بيانات اللوحة</span>);
    } finally {
      setLoadingProduct(false);
    }
  }, []);

  useEffect(() => {
    if (preselectedId) fetchProductById(preselectedId);
  }, [preselectedId, fetchProductById]);

  // Horizontal design strip: AR-ready products, optionally filtered by category,
  // loaded page by page as the strip is scrolled.
  const galleryKey = (pageIndex, prev) => {
    if (step !== STEPS.PLACE || galleryTab !== "all") return null;
    if (prev && pageIndex * GALLERY_LIMIT >= (prev.totalCount ?? 0)) return null;
    const category = galleryCategory ? `&category=${galleryCategory}` : "";
    return `${process.env.REACT_APP_BACKEND_URL}/api/products?page=${pageIndex + 1}&limit=${GALLERY_LIMIT}&hasAr=true${category}`;
  };

  const {
    data: galleryPages,
    size: galleryPageCount,
    setSize: setGalleryPageCount,
    isValidating: galleryLoading,
    error: galleryError,
  } = useSWRInfinite(galleryKey, fetcher, {
    revalidateOnFocus: false,
    revalidateFirstPage: false,
    shouldRetryOnError: (err) => err.status !== 304,
    errorRetryCount: 2,
  });

  const galleryProducts = useMemo(() => (galleryPages ?? []).flatMap((p) => p?.products ?? []), [galleryPages]);
  const galleryLowestPrices = useMemo(() => (galleryPages ?? []).flatMap((p) => p?.lowestPrices ?? []), [galleryPages]);
  const galleryHasMore = galleryProducts.length < (galleryPages?.[galleryPages.length - 1]?.totalCount ?? 0);

  const { data: categoriesData } = useSWR(
    step === STEPS.PLACE ? `${process.env.REACT_APP_BACKEND_URL}/api/categories` : null,
    fetcher,
    { revalidateOnFocus: false },
  );
  const categories = Array.isArray(categoriesData) ? categoriesData : [];

  const handleWallFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 8 * 1024 * 1024) {
      toast.error(<span className="toast">حجم الصورة يجب أن يكون أقل من 8 ميجابايت</span>);
      e.target.value = "";
      return;
    }

    setWallFile(file);
    setWallCorners(DEFAULT_WALL_CORNERS);
    const reader = new FileReader();
    reader.onloadend = () => setWallPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const removeWallFile = () => {
    setWallFile(null);
    setWallPreview(null);
    setWallImgSize({ width: 0, height: 0 });
    setWallCorners(DEFAULT_WALL_CORNERS);
  };

  const goToOutlineStep = () => {
    if (!wallFile) {
      toast.error(<span className="toast">إضافة صورة الحائط مطلوبة</span>);
      return;
    }
    setStep(STEPS.OUTLINE);
  };

  const goToPlaceStep = () => {
    if (!wallWidth || Number(wallWidth) <= 0) {
      toast.error(<span className="toast">اكتب عرض الحائط اولاً</span>);
      return;
    }
    setStep(STEPS.PLACE);
  };

  const selectGalleryProduct = (product) => {
    if (product.product_id === selectedProduct?.productId || loadingProduct) return;
    fetchProductById(product.product_id);
  };

  const handleWallImgLoad = (e) => {
    setWallNatural({ width: e.target.naturalWidth, height: e.target.naturalHeight });
    setWallImgSize({ width: e.target.clientWidth, height: e.target.clientHeight });
  };

  const handleOutlineImgLoad = (e) => {
    setOutlineImgSize({ width: e.target.clientWidth, height: e.target.clientHeight });
  };

  useEffect(() => {
    if (step !== STEPS.OUTLINE || !outlineImgRef.current) return;

    const el = outlineImgRef.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setOutlineImgSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
    // outlineScene (and its <img ref={outlineImgRef}>) only ever mounts in one place at
    // a time — normal or maximized — so this must re-run on toggle to reattach to the
    // freshly mounted node instead of observing a now-unmounted element.
  }, [step, wallPreview, maximized]);

  // Drag a single outline corner (as a fraction of the outline photo), clamped to the
  // image bounds so handles can't be dragged off it.
  const handleCornerDragStart = (corner) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!outlineImgSize.width || !outlineImgSize.height) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const start = wallCorners[corner];

    const onMove = (ev) => {
      const dxPct = (ev.clientX - startX) / outlineImgSize.width;
      const dyPct = (ev.clientY - startY) / outlineImgSize.height;
      setWallCorners((c) => ({
        ...c,
        [corner]: {
          x: Math.min(1, Math.max(0, start.x + dxPct)),
          y: Math.min(1, Math.max(0, start.y + dyPct)),
        },
      }));
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // A ResizeObserver (rather than a window "resize" listener) catches every reason
  // the rendered image box can change size — window resize, but also toggling the
  // maximized lightbox, which changes layout without firing a resize event at all.
  useEffect(() => {
    if (step !== STEPS.PLACE || !wallImgRef.current) return;

    const el = wallImgRef.current;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setWallImgSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
    // wallScene (and its <img ref={wallImgRef}>) only ever mounts in one place at a
    // time — normal or maximized — so this must re-run on toggle to reattach to the
    // freshly mounted node instead of observing a now-unmounted element.
  }, [step, wallPreview, maximized]);

  // Phones: size the Place step to exactly the screen space below where it starts
  // (navbar + page title), measured rather than estimated, so the buy buttons are
  // always on screen. Re-measured on resize and when the navbar changes height.
  useEffect(() => {
    const el = studioRef.current;
    if (step !== STEPS.PLACE || !el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      el.style.setProperty("--studio-top", `${Math.round(top)}px`);
    };
    measure();
    window.addEventListener("resize", measure);
    const header = document.querySelector("header");
    const ro = header && "ResizeObserver" in window ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(header);
    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [step]);

  useEffect(() => {
    if (step !== STEPS.PLACE || !sceneRef.current) return;
    const el = sceneRef.current;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect ?? {};
      if (width > 0 && height > 0) setSceneSize({ width, height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [step]);

  useEffect(() => {
    if (!selectedProduct?.image_url) return;
    const img = new Image();
    img.onload = () => setNaturalAspect(img.naturalWidth / img.naturalHeight || 1);
    img.src = mediaUrl(selectedProduct.image_url);
  }, [selectedProduct]);

  // Place step: the wall's homography (and inverse, for translating pointer positions
  // back into its flat plane) at the Place step's own rendered pixel size — which can
  // differ from the size the corners were traced at in the Outline step.
  const { homography: wallHomography, pxSize: quadPxSize } = useQuadGeometry(wallCorners, wallImgSize);
  const wallHomographyInv = useMemo(() => (wallHomography ? invert3x3(wallHomography) : null), [wallHomography]);

  // Outline step: the same geometry, but at that step's own rendered size — used only
  // for the generic "imagine a printing here" placeholder preview.
  const { homography: outlineHomography, pxSize: outlineQuadPxSize } = useQuadGeometry(wallCorners, outlineImgSize);

  const placeholderPreview = useMemo(() => {
    if (!outlineHomography || !wallWidth || !outlineQuadPxSize.width || !outlineQuadPxSize.height) return null;
    const heightPct = (PLACEHOLDER_WIDTH_PCT * outlineQuadPxSize.width) / (PLACEHOLDER_ASPECT * outlineQuadPxSize.height);
    const leftPct = (1 - PLACEHOLDER_WIDTH_PCT) / 2;
    const topPct = Math.max(0, (1 - heightPct) / 2);
    const project = (u, v) => applyPoint(outlineHomography, u, v);
    const tl = project(leftPct, topPct);
    const tr = project(leftPct + PLACEHOLDER_WIDTH_PCT, topPct);
    const bl = project(leftPct, topPct + heightPct);
    const br = project(leftPct + PLACEHOLDER_WIDTH_PCT, topPct + heightPct);
    const w = Math.hypot(tr[0] - tl[0], tr[1] - tl[1]) || 1;
    const h = Math.hypot(bl[0] - tl[0], bl[1] - tl[1]) || 1;
    return {
      matrix: quadMatrix3d(w, h, { tl, tr, bl, br }),
      width: w,
      height: h,
      sizeCm: {
        width: Math.round(PLACEHOLDER_WIDTH_PCT * Number(wallWidth) * 100),
        height: Math.round((PLACEHOLDER_WIDTH_PCT * Number(wallWidth) * 100) / PLACEHOLDER_ASPECT),
      },
    };
  }, [outlineHomography, outlineQuadPxSize, wallWidth]);

  // Free/custom sizing only: center a sensible starting size once the wall photo and
  // artwork are both loaded. Preset sizing is initialized by selectDim() instead, once
  // a catalog dimension is known — see the effect below.
  useEffect(() => {
    if (sizingMode !== "custom") return;
    if (!quadPxSize.width || !naturalAspect || overlay.widthPct) return;
    const heightPct = (INITIAL_OVERLAY_WIDTH_PCT * quadPxSize.width) / naturalAspect / quadPxSize.height;
    setOverlay({
      leftPct: (1 - INITIAL_OVERLAY_WIDTH_PCT) / 2,
      topPct: Math.max(0, (1 - heightPct) / 2),
      widthPct: INITIAL_OVERLAY_WIDTH_PCT,
    });
  }, [sizingMode, quadPxSize, naturalAspect, overlay.widthPct]);

  useEffect(() => {
    document.body.style.overflow = maximized ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [maximized]);

  // The maximized lightbox is shared between the Outline and Place steps (it shows
  // whichever step's own scene is current) — reset it on every step change so it can
  // never carry over stale and show the wrong content.
  useEffect(() => {
    setMaximized(false);
  }, [step]);

  // Every step (and the success screen) starts at the top of the page, even if the
  // previous step was scrolled down when "التالى" / "رجوع" was pressed.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step, success]);

  // Snap the overlay to a catalog dimension's real width/height ratio (not the
  // artwork image's own ratio — the two can differ slightly across sizes) and center it.
  const selectDim = useCallback(
    (dim) => {
      setSelectedDim(dim);
      if (!wallWidth || !quadPxSize.width || !quadPxSize.height) return;
      // Capped to the outlined area itself — a catalog size wider than the traced wall
      // would otherwise center outside it.
      const widthPct = Math.min(dim.width / (Number(wallWidth) * 100), 1);
      const widthPx = widthPct * quadPxSize.width;
      const heightPx = widthPx * (dim.height / dim.width);
      const heightPct = Math.min(heightPx / quadPxSize.height, 1);
      setOverlay({ leftPct: Math.max(0, (1 - widthPct) / 2), topPct: Math.max(0, (1 - heightPct) / 2), widthPct });
    },
    [wallWidth, quadPxSize],
  );

  // Default to the cheapest catalog size as soon as we can compute its geometry.
  useEffect(() => {
    if (sizingMode !== "preset" || selectedDim || !quadPxSize.width) return;
    if (!selectedProduct?.dimensions?.length) return;
    const cheapest = [...selectedProduct.dimensions].sort((a, b) => a.matte_price - b.matte_price)[0];
    selectDim(cheapest);
  }, [sizingMode, selectedDim, quadPxSize.width, selectedProduct, selectDim]);

  // The real aspect ratio to render at: a chosen catalog size's own width/height in
  // preset mode (sizes don't always share the artwork's exact ratio), otherwise the
  // artwork image's natural ratio for free/custom sizing.
  const effectiveAspect = sizingMode === "preset" && selectedDim ? selectedDim.width / selectedDim.height : naturalAspect;

  // The overlay's height as a fraction of the outline quad, derived from its real-world
  // aspect ratio and the quad's own pixel aspect (which stands in for its real one).
  const overlayHeightPct =
    quadPxSize.width && quadPxSize.height ? (overlay.widthPct * quadPxSize.width) / (effectiveAspect * quadPxSize.height) : 0;

  // Preset mode with a chosen size that has its own dedicated AR photo: show that exact
  // photo instead of stretching the free/general one to fit — it's shot (or cropped) for
  // that size specifically, so it doesn't lose resolution the way a generic photo scaled
  // up to any size can. Falls back to the free photo (or catalog image) otherwise —
  // custom sizing, or a preset size the admin hasn't uploaded a dedicated photo for yet.
  const activeArImage =
    sizingMode === "preset" && selectedDim?.ar_image_url ? selectedDim.ar_image_url : selectedProduct?.image_url;

  // Dragging/resizing is confined to the outlined wall area itself — the artwork can't
  // be moved or grown past the edges the user traced.
  const handleDragStart = (e) => {
    e.preventDefault();
    if (!wallHomographyInv || !wallImgRef.current) return;
    const rect = wallImgRef.current.getBoundingClientRect();
    const toUV = (clientX, clientY) => applyPoint(wallHomographyInv, clientX - rect.left, clientY - rect.top);
    const [startU, startV] = toUV(e.clientX, e.clientY);
    const startLeftPct = overlay.leftPct;
    const startTopPct = overlay.topPct;
    const maxLeftPct = Math.max(0, 1 - overlay.widthPct);
    const maxTopPct = Math.max(0, 1 - overlayHeightPct);
    setInteracting(true);

    const onMove = (ev) => {
      const [u, v] = toUV(ev.clientX, ev.clientY);
      setOverlay((o) => ({
        ...o,
        leftPct: Math.min(Math.max(startLeftPct + (u - startU), 0), maxLeftPct),
        topPct: Math.min(Math.max(startTopPct + (v - startV), 0), maxTopPct),
      }));
    };
    const onUp = () => {
      setInteracting(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!wallHomographyInv || !wallImgRef.current) return;
    const rect = wallImgRef.current.getBoundingClientRect();
    const toUV = (clientX, clientY) => applyPoint(wallHomographyInv, clientX - rect.left, clientY - rect.top);
    const [startU] = toUV(e.clientX, e.clientY);
    const startWidthPct = overlay.widthPct;
    // Resizing anchors at the top-left corner, so growth is capped by both the right
    // edge (leftPct + widthPct <= 1) and the bottom edge (topPct + heightPct <= 1).
    const maxWidthByRight = Math.max(MIN_OVERLAY_WIDTH_PCT, 1 - overlay.leftPct);
    const maxHeightAllowed = Math.max(0, 1 - overlay.topPct);
    const maxWidthByBottom =
      quadPxSize.width && quadPxSize.height
        ? Math.max(MIN_OVERLAY_WIDTH_PCT, (maxHeightAllowed * effectiveAspect * quadPxSize.height) / quadPxSize.width)
        : Infinity;
    const maxWidthPct = Math.min(maxWidthByRight, maxWidthByBottom);
    setInteracting(true);

    const onMove = (ev) => {
      const [u] = toUV(ev.clientX, ev.clientY);
      const newWidthPct = Math.min(Math.max(MIN_OVERLAY_WIDTH_PCT, startWidthPct + (u - startU)), maxWidthPct);
      setOverlay((o) => ({ ...o, widthPct: newWidthPct }));
    };
    const onUp = () => {
      setInteracting(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // Resolution-independent: derived straight from the fraction of the wall's real
  // width, so it doesn't need (or drift with) the rendered pixel size.
  const sizeCm = wallWidth
    ? {
        width: Math.round(overlay.widthPct * Number(wallWidth) * 100),
        height: Math.round((overlay.widthPct * Number(wallWidth) * 100) / effectiveAspect),
      }
    : { width: 0, height: 0 };

  // Project the overlay's flat-plane rectangle through the wall's homography to get its
  // actual (possibly tilted/skewed) corners in the photo, then build the CSS transform
  // that warps the overlay's own box onto those corners.
  const overlayTransform = useMemo(() => {
    if (!wallHomography || !overlay.widthPct || !overlayHeightPct) return null;
    const project = (u, v) => applyPoint(wallHomography, u, v);
    const tl = project(overlay.leftPct, overlay.topPct);
    const tr = project(overlay.leftPct + overlay.widthPct, overlay.topPct);
    const bl = project(overlay.leftPct, overlay.topPct + overlayHeightPct);
    const br = project(overlay.leftPct + overlay.widthPct, overlay.topPct + overlayHeightPct);
    const w = Math.hypot(tr[0] - tl[0], tr[1] - tl[1]) || 1;
    const h = Math.hypot(bl[0] - tl[0], bl[1] - tl[1]) || 1;
    return { matrix: quadMatrix3d(w, h, { tl, tr, bl, br }), width: w, height: h };
  }, [wallHomography, overlay, overlayHeightPct]);

  const presetPrice = selectedDim ? (material === "gloss" ? selectedDim.gloss_price : selectedDim.matte_price) : 0;

  // No design chosen yet (not opened from a product page): start with the first one.
  // Only tried once, so a failed load doesn't retry in a loop.
  const autoPickedRef = useRef(false);
  useEffect(() => {
    if (step !== STEPS.PLACE || selectedProduct || loadingProduct || preselectedId || autoPickedRef.current) return;
    if (galleryProducts[0]) {
      autoPickedRef.current = true;
      fetchProductById(galleryProducts[0].product_id);
    }
  }, [step, selectedProduct, loadingProduct, preselectedId, galleryProducts, fetchProductById]);

  // The chosen design as a product card (the shape the wishlist stores)
  const selectedCard = selectedProduct
    ? galleryProducts.find((p) => p.product_id === selectedProduct.productId) ??
      wishlist.find((w) => w.product.product_id === selectedProduct.productId)?.product ?? {
        product_id: selectedProduct.productId,
        name: selectedProduct.name,
        image_url: selectedProduct.catalogImage,
        ar_image_url: selectedProduct.image_url,
      }
    : null;

  const isWished = (productId) => wishlist.some((w) => w.product.product_id === productId);

  const toggleWish = (product) => {
    if (!product) return;
    if (isWished(product.product_id)) {
      dispatch(removeProduct(product));
    } else {
      const lowestPrice = galleryLowestPrices.find((lp) => lp.product_id === product.product_id)?.min_price;
      dispatch(addProduct({ product, lowestPrice }));
    }
  };

  // Strip contents: the "all" list (keeping an opened-from-product design visible even
  // if it isn't on the loaded pages), or the visitor's wishlist.
  const stripProducts =
    galleryTab === "wishlist"
      ? wishlist.map((w) => w.product)
      : selectedCard && !galleryCategory && !galleryProducts.some((p) => p.product_id === selectedCard.product_id)
        ? [selectedCard, ...galleryProducts]
        : galleryProducts;

  const handleStripScroll = (e) => {
    const el = e.currentTarget;
    // RTL: scrollLeft runs from 0 towards negative values as you scroll
    const remaining = el.scrollWidth - el.clientWidth - Math.abs(el.scrollLeft);
    if (remaining < 200 && galleryHasMore && !galleryLoading) setGalleryPageCount(galleryPageCount + 1);
  };

  const buildCartItem = () => ({
    id: selectedProduct.productId,
    name: selectedProduct.name,
    image_url: selectedProduct.catalogImage ?? selectedProduct.image_url,
    dimension: selectedDim,
    material,
    price: presetPrice,
    quantity: 1,
  });

  const handleAddToCartPreset = () => {
    if (!selectedDim) {
      toast.error(<span className="toast">اختر المقاس اولاً</span>);
      return;
    }
    if (cartItems.length >= 30) {
      toast.error(<span className="toast">لقد وصلت للحد الأقصى للمنتجات فى السلة</span>);
      return;
    }
    if (
      cartItems?.some(
        (item) => item.dimension.dim_id === selectedDim.dim_id && item.material === material && item.id === selectedProduct.productId,
      )
    ) {
      toast.success(<span className="toast">المنتج موجود بالسلة بالفعل</span>);
      return;
    }

    dispatch(addToCart(buildCartItem()));
    toast.success(<span className="toast">تم إضافة المنتج إلى السلة بنجاح</span>);
  };

  const handleBuyNowPreset = () => {
    if (!selectedDim) {
      toast.error(<span className="toast">اختر المقاس اولاً</span>);
      return;
    }
    if (!user) {
      navigate("/login");
      return;
    }
    if (!user.name || !user.mobile || !user.street) {
      navigate("/account", { state: { fromCart: true } });
      return;
    }

    navigate("/checkout", { state: { fromCart: true, directBuy: buildCartItem() } });
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error(<span className="toast">قم بتسجيل الدخول اولا</span>);
      return;
    }
    if (!name.trim() || !mobile.trim()) {
      toast.error(<span className="toast">اكتب اسمك ورقم هاتفك</span>);
      return;
    }

    const shipping_details = { name: name.trim(), mobile: mobile.trim() };
    const order = {
      wallWidth,
      requestType: "product_size",
      productId: selectedProduct.productId,
      width: sizeCm.width,
      height: sizeCm.height,
      material,
    };

    const formData = new FormData();
    formData.append("shipping_details", JSON.stringify(shipping_details));
    formData.append("order", JSON.stringify(order));
    formData.append("decorImg", wallFile);

    confirmAlert({
      message: "تأكيد إرسال الطلب ؟",
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setSubmitting(true);
            try {
              await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders/addSpecial`, formData, {
                withCredentials: true,
              });
              setSuccess(true);
            } catch (error) {
              toast.error(<span className="toast">لم نتمكن من إرسال الطلب. حاول مرة اخرى</span>);
            } finally {
              setSubmitting(false);
            }
          },
        },
        { label: "لا" },
      ],
    });
  };

  // The top edge of the outline (TL→TR), in real rendered pixels — drawn as a distinct
  // "measurement line" so the width the user types clearly refers to that edge. Computed
  // in real pixel space (not the SVG's 0-100 viewBox) so the angle isn't skewed by a
  // non-square image being stretched into a square coordinate space.
  const outlineTlPx = { x: wallCorners.tl.x * outlineImgSize.width, y: wallCorners.tl.y * outlineImgSize.height };
  const outlineTrPx = { x: wallCorners.tr.x * outlineImgSize.width, y: wallCorners.tr.y * outlineImgSize.height };
  const widthLineLength = Math.hypot(outlineTrPx.x - outlineTlPx.x, outlineTrPx.y - outlineTlPx.y);
  const widthLineAngle = (Math.atan2(outlineTrPx.y - outlineTlPx.y, outlineTrPx.x - outlineTlPx.x) * 180) / Math.PI;

  const outlineScene = (
    <div className={styles.wallImgWrap}>
      <img ref={outlineImgRef} src={wallPreview} alt="wall" onLoad={handleOutlineImgLoad} className={styles.wallImg} />

      {outlineImgSize.width > 0 && (
        <>
          <svg className={styles.outlineSvg} viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon
              className={styles.outlinePolygon}
              points={`${wallCorners.tl.x * 100},${wallCorners.tl.y * 100} ${wallCorners.tr.x * 100},${wallCorners.tr.y * 100} ${wallCorners.br.x * 100},${wallCorners.br.y * 100} ${wallCorners.bl.x * 100},${wallCorners.bl.y * 100}`}
            />
          </svg>

          <span
            className={styles.widthLine}
            style={{
              left: `${wallCorners.tl.x * 100}%`,
              top: `${wallCorners.tl.y * 100}%`,
              width: widthLineLength,
              transform: `rotate(${widthLineAngle}deg)`,
            }}
          />
          <span className={styles.widthLineDot} style={{ left: `${wallCorners.tl.x * 100}%`, top: `${wallCorners.tl.y * 100}%` }} />
          <span className={styles.widthLineDot} style={{ left: `${wallCorners.tr.x * 100}%`, top: `${wallCorners.tr.y * 100}%` }} />
          {wallWidth && Number(wallWidth) > 0 && (
            <span
              className={styles.widthLineLabel}
              style={{ left: `${((wallCorners.tl.x + wallCorners.tr.x) / 2) * 100}%`, top: `${((wallCorners.tl.y + wallCorners.tr.y) / 2) * 100}%` }}
            >
              {wallWidth} م
            </span>
          )}

          {placeholderPreview && (
            <div
              className={styles.placeholderBox}
              style={{
                left: 0,
                top: 0,
                width: placeholderPreview.width,
                height: placeholderPreview.height,
                transform: placeholderPreview.matrix,
                transformOrigin: "0 0",
              }}
            >
              <FiImage />
              <span>
                {placeholderPreview.sizeCm.width} × {placeholderPreview.sizeCm.height} سم (مثال)
              </span>
            </div>
          )}

          {["tl", "tr", "bl", "br"].map((corner) => (
            <span
              key={corner}
              className={styles.cornerHandle}
              style={{ left: `${wallCorners[corner].x * 100}%`, top: `${wallCorners[corner].y * 100}%` }}
              onPointerDown={handleCornerDragStart(corner)}
            />
          ))}
        </>
      )}
    </div>
  );

  const coverBox = (() => {
    if (!isPhone || step !== STEPS.PLACE || maximized) return null;
    if (!sceneSize.width || !sceneSize.height || !wallNatural.width || !wallNatural.height) return null;
    const scale = Math.max(sceneSize.width / wallNatural.width, sceneSize.height / wallNatural.height);
    const width = wallNatural.width * scale;
    const height = wallNatural.height * scale;
    // Keep the outlined wall centred in view, without showing empty space at the edges
    const cx = (wallCorners.tl.x + wallCorners.tr.x + wallCorners.bl.x + wallCorners.br.x) / 4;
    const cy = (wallCorners.tl.y + wallCorners.tr.y + wallCorners.bl.y + wallCorners.br.y) / 4;
    const clamp = (v, min) => Math.min(0, Math.max(min, v));
    return {
      width,
      height,
      left: clamp(sceneSize.width / 2 - cx * width, sceneSize.width - width),
      top: clamp(sceneSize.height / 2 - cy * height, sceneSize.height - height),
    };
  })();

  const wallScene = (
    <div
      className={styles.wallImgWrap}
      style={coverBox ? { position: "absolute", left: coverBox.left, top: coverBox.top, width: coverBox.width, height: coverBox.height } : undefined}
    >
      <img
        ref={wallImgRef}
        src={wallPreview}
        alt="wall"
        onLoad={handleWallImgLoad}
        className={styles.wallImg}
        style={coverBox ? { width: "100%", height: "100%" } : undefined}
      />

      {overlayTransform && (
        <div
          className={`${styles.overlay} ${interacting ? styles.overlayActive : ""}`}
          style={{
            left: 0,
            top: 0,
            width: overlayTransform.width,
            height: overlayTransform.height,
            transform: overlayTransform.matrix,
            transformOrigin: "0 0",
          }}
          onPointerDown={handleDragStart}
        >
          <img src={mediaUrl(activeArImage)} alt={selectedProduct?.name} draggable={false} />
          <span className={styles.overlayGloss} />
          <span className={`${styles.sizeLabel} ${interacting ? styles.sizeLabelVisible : ""}`}>
            {sizeCm.width} × {sizeCm.height} سم
          </span>
          {sizingMode === "custom" && <span className={styles.resizeHandle} onPointerDown={handleResizeStart} />}
        </div>
      )}
    </div>
  );

  return (
    <div className={`${styles.page} ${step === STEPS.PLACE && !success ? styles.pagePlace : ""}`}>
      <div className={styles.header}>
        <span className={styles.headerSide}>
          {step === STEPS.PLACE && selectedCard && !success && (
            <button type="button" className={styles.headerWish} onClick={() => toggleWish(selectedCard)} aria-label="المفضلة">
              <FiHeart className={isWished(selectedCard.product_id) ? styles.wished : ""} />
            </button>
          )}
        </span>
        <h4>شاهدها على حائطك</h4>
        <span className={`${styles.headerSide} ${styles.headerSideEnd}`}>
          <MdClose className={styles.closeIcon} onClick={() => navigate(-1)} />
        </span>
      </div>

      {success ? (
        <div className={styles.successCard}>
          <div className={styles.successBadge}>
            <span className={styles.successBadgeRing} />
            <FiCheck />
          </div>

          <h3 className={styles.successTitle}>تم إرسال طلبك بنجاح</h3>
          <p className={styles.successSubtitle}>سيقوم فريقنا بمراجعة المقاس والتواصل معك بأقرب وقت لتأكيد السعر والطلب</p>

          <div className={styles.successRecap}>
            <img src={mediaUrl(activeArImage)} alt={selectedProduct?.name} />
            <div className={styles.successRecapInfo}>
              <strong>{selectedProduct?.name}</strong>
              <span>{sizeCm.width} × {sizeCm.height} سم</span>
              <span>{material === "gloss" ? "Luster VIP" : "Matte"}</span>
            </div>
          </div>

          <div className={styles.successSteps}>
            <div className={`${styles.successStep} ${styles.successStepDone}`}>
              <span className={styles.successStepDot}><FiCheck /></span>
              <p>تم استلام طلبك</p>
            </div>
            <div className={`${styles.successStepLine} ${styles.successStepLineFilled}`} />
            <div className={`${styles.successStep} ${styles.successStepActive}`}>
              <span className={styles.successStepDot}>2</span>
              <p>مراجعة وتسعير</p>
            </div>
            <div className={styles.successStepLine} />
            <div className={styles.successStep}>
              <span className={styles.successStepDot}>3</span>
              <p>نتواصل معك</p>
            </div>
          </div>

          <div className={styles.stepActions}>
            <button type="button" className={styles.backBtn} onClick={() => navigate("/orders")}>
              عرض طلباتى
            </button>
            <button type="button" className={styles.primaryBtn} onClick={() => navigate("/products")}>
              تصفح المزيد من اللوحات
            </button>
          </div>
        </div>
      ) : (
        <>
          {step === STEPS.WALL && (
            <div className={`${styles.card} ${styles.wallStep}`}>
              <p className={styles.stepTitle}>1 - صورة الحائط</p>

              <div className={`${styles.field} ${styles.wallField}`}>
                {/* <label>صورة الحائط</label> */}
                {wallPreview ? (
                  <div className={styles.wallPreviewBox}>
                    <img src={wallPreview} alt="wall" />
                    <button type="button" className={styles.removeBtn} onClick={removeWallFile}>
                      <FaRegTrashAlt />
                    </button>
                  </div>
                ) : (
                  <button type="button" className={styles.uploadBox} onClick={() => wallInputRef.current.click()}>
                    <FiImage />
                    <span>إضافة صورة الحائط</span>
                  </button>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={wallInputRef}
                  onChange={handleWallFileChange}
                  style={{ display: "none" }}
                />
              </div>

              <div className={styles.tips}>
                {WALL_TIPS.map((tip) => (
                  <div key={tip.text} className={styles.tip}>
                    {tip.icon}
                    {tip.text}
                  </div>
                ))}
              </div>

              <button type="button" className={styles.primaryBtn} onClick={goToOutlineStep}>
                التالى
              </button>
            </div>
          )}

          {step === STEPS.OUTLINE && (
            <div className={`${styles.card} ${styles.outlineStep}`}>
              <p className={styles.stepTitle}>2 - حدد شكل الحائط وعرضه</p>
              {/* <p className={styles.hint}>
                اسحب النقاط الأربع لتحديد حواف الحائط فى الصورة — هذا يساعد فى إمالة اللوحة لتطابق زاوية الحائط إذا كانت الصورة مأخوذة من
                جانب. الخط العلوى يمثل عرض الحائط الذى تكتبه بالأسفل
              </p> */}

              <div className={styles.wallContainer}>
                {!maximized && (
                  <>
                    {outlineScene}
                    <button
                      type="button"
                      className={styles.maximizeBtn}
                      aria-label="تكبير"
                      onClick={() => setMaximized(true)}
                    >
                      <FiMaximize2 />
                    </button>
                  </>
                )}
              </div>

              <button type="button" className={styles.addToCartLink} onClick={() => setWallCorners(DEFAULT_WALL_CORNERS)}>
                إعادة تعيين الحدود
              </button>

              <div className={styles.field}>
                <label>عرض الحائط بالمتر (طول الخط العلوى)</label>
                <input
                  type="number"
                  step={0.01}
                  min={1}
                  max={65535}
                  value={wallWidth}
                  onChange={(e) => setWallWidth(e.target.value)}
                  placeholder="مثال: 3.5"
                  className={styles.input}
                />
              </div>

              <div className={styles.stepActions}>
                <button type="button" className={styles.backBtn} onClick={() => setStep(STEPS.WALL)}>
                  السابق
                </button>
                <button type="button" className={styles.primaryBtn} onClick={goToPlaceStep}>
                  التالى
                </button>
              </div>
            </div>
          )}

          {step === STEPS.PLACE && (
            <div ref={studioRef} className={styles.studio}>
              {/* ── Wall with the design on it ── */}
              <div ref={sceneRef} className={styles.stScene}>
                {/* The wall is mounted in one place at a time: here, or in the maximized view */}
                {!maximized && wallScene}

                <div className={styles.stCorner}>
                  <button
                    type="button"
                    className={styles.stIconBtn}
                    onClick={() => setMaximized(true)}
                    aria-label="تكبير"
                    title="تكبير"
                    disabled={!selectedProduct || loadingProduct}
                  >
                    <FiMaximize2 />
                  </button>
                  <button
                    type="button"
                    className={`${styles.stIconBtn} ${styles.stIconBtnSmall}`}
                    onClick={() => setStep(STEPS.WALL)}
                    aria-label="تغيير الحائط"
                    title="تغيير الحائط"
                  >
                    <FiCamera />
                  </button>
                </div>

                {loadingProduct && (
                  <div className={styles.stLoading}>
                    <ImSpinner2 className={styles.stSpinner} />
                  </div>
                )}
              </div>

              {/* ── Sizes + prices (under the wall, so nothing covers it) ── */}
              {selectedProduct && !loadingProduct && (
                <div className={styles.stSizeRow}>
                  <RiRulerLine className={styles.stSizeRowIcon} aria-label="مقاس + سعر" />
                  <div className={styles.stChips}>
                    {selectedProduct.dimensions.map((dim) => {
                      const active = sizingMode === "preset" && selectedDim?.id === dim.id;
                      return (
                        <button
                          key={dim.id}
                          type="button"
                          className={`${styles.stChip} ${active ? styles.stChipActive : ""}`}
                          onClick={() => {
                            setSizingMode("preset");
                            selectDim(dim);
                          }}
                        >
                          <span className={styles.stChipDim}>{dim.width}×{dim.height}</span>
                          <span className={styles.stChipPrice}>₪{material === "gloss" ? dim.gloss_price : dim.matte_price}</span>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className={`${styles.stChip} ${styles.stChipCustom} ${sizingMode === "custom" ? styles.stChipActive : ""}`}
                      onClick={() => setSizingMode("custom")}
                    >
                      <FiTool />
                      <span className={styles.stChipDim}>تفصيل خاص</span>
                    </button>
                  </div>
                </div>
              )}
              {sizingMode === "custom" && selectedProduct && (
                <p className={styles.stCustomHint}>
                  <strong>{sizeCm.width} × {sizeCm.height} سم</strong> — اسحب المقبض أسفل اللوحة لتغيير المقاس
                </p>
              )}

              {/* ── Summary: size | material | price ── */}
              {selectedProduct && (
                <div className={styles.stSummary}>
                  <span className={styles.stSummaryItem}>
                    <FiCrop />
                    {sizeCm.width} × {sizeCm.height}
                  </span>
                  <span className={styles.stSummarySep} />
                  <span className={styles.stSummaryItem}>{material === "gloss" ? "Luster" : "Matte"}</span>
                  <span className={styles.stSummarySep} />
                  <strong className={styles.stSummaryPrice}>
                    {sizingMode === "preset" && selectedDim ? `₪ ${presetPrice}` : "يُسعَّر بعد المراجعة"}
                  </strong>
                </div>
              )}

              {/* ── Material ── */}
              <div className={styles.stMaterial}>
                <button
                  type="button"
                  className={`${styles.stMaterialBtn} ${material === "matte" ? styles.stMaterialActive : ""}`}
                  onClick={() => setMaterial("matte")}
                >
                  Matte
                </button>
                <button
                  type="button"
                  className={`${styles.stMaterialBtn} ${material === "gloss" ? styles.stMaterialActive : ""}`}
                  onClick={() => setMaterial("gloss")}
                >
                  Luster
                </button>
              </div>

              {/* ── Design filters ── */}
              <div className={styles.stFilters}>
                <div className={styles.stTabs}>
                  <button
                    type="button"
                    className={`${styles.stTab} ${galleryTab === "all" ? styles.stTabActive : ""}`}
                    onClick={() => setGalleryTab("all")}
                  >
                    الكل
                  </button>
                  <span className={styles.stTabsSep} />
                  <button
                    type="button"
                    className={`${styles.stTab} ${galleryTab === "wishlist" ? styles.stTabActive : ""}`}
                    onClick={() => setGalleryTab("wishlist")}
                  >
                    المفضلة
                    <FiHeart />
                  </button>
                </div>

                <div className={styles.stFilterWrap}>
                  <button
                    type="button"
                    className={`${styles.stFilterBtn} ${galleryCategory ? styles.stFilterBtnOn : ""}`}
                    onClick={() => setFilterOpen((o) => !o)}
                    disabled={galleryTab !== "all"}
                  >
                    <FiChevronDown />
                    <span>
                      {galleryCategory
                        ? categories.find((c) => String(c.id) === String(galleryCategory))?.name ?? "تصفية التصاميم"
                        : "تصفية التصاميم"}
                    </span>
                    <FiSliders />
                  </button>

                  {filterOpen && galleryTab === "all" && (
                    <div className={styles.stFilterMenu}>
                      {[{ id: "", name: "كل التصاميم" }, ...categories].map((cat) => (
                        <button
                          key={cat.id || "all"}
                          type="button"
                          className={`${styles.stFilterOption} ${String(galleryCategory) === String(cat.id) ? styles.stFilterOptionOn : ""}`}
                          onClick={() => {
                            setGalleryCategory(cat.id);
                            setFilterOpen(false);
                          }}
                        >
                          {cat.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Design strip ── */}
              <div className={styles.stStrip} onScroll={handleStripScroll}>
                {stripProducts.map((product) => (
                  <div
                    key={product.product_id}
                    role="button"
                    tabIndex={0}
                    className={`${styles.stThumb} ${product.product_id === selectedProduct?.productId ? styles.stThumbActive : ""}`}
                    onClick={() => selectGalleryProduct(product)}
                    onKeyDown={(e) => e.key === "Enter" && selectGalleryProduct(product)}
                  >
                    <ProductImage url={product.ar_image_url ?? product.image_url} alt={product.name} loading="lazy" />
                    <button
                      type="button"
                      className={styles.stThumbWish}
                      aria-label="المفضلة"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleWish(product);
                      }}
                    >
                      <FiHeart className={isWished(product.product_id) ? styles.wished : ""} />
                    </button>
                  </div>
                ))}

                {galleryTab === "all" && galleryLoading &&
                  Array.from({ length: stripProducts.length ? 2 : 5 }).map((_, i) => (
                    <div key={`sk-${i}`} className={`${styles.stThumb} ${styles.stThumbSkeleton}`} />
                  ))}
              </div>

              {!galleryLoading && stripProducts.length === 0 && (
                <p className={styles.stEmpty}>
                  {galleryTab === "wishlist"
                    ? "لا توجد تصاميم في المفضلة بعد — اضغط على القلب لإضافتها"
                    : galleryError && galleryError.status !== 304
                      ? "حدث خطأ اثناء تحميل اللوحات. حاول مرة اخرى"
                      : "لا توجد لوحات متاحة لهذه الميزة حالياً"}
                </p>
              )}

              {/* ── Actions ── */}
              {sizingMode === "preset" ? (
                <div className={styles.stActions}>
                  <button type="button" className={styles.stBuy} onClick={handleBuyNowPreset} disabled={!selectedDim}>
                    <BsLightningChargeFill />
                    أشترِ الآن
                  </button>
                  <button type="button" className={styles.stCart} onClick={handleAddToCartPreset} disabled={!selectedDim}>
                    <FiShoppingCart />
                    أضف إلى السلة
                  </button>
                </div>
              ) : (
                <div className={styles.stActions}>
                  <button
                    type="button"
                    className={styles.stBuy}
                    onClick={() => setStep(STEPS.CONFIRM)}
                    disabled={!selectedProduct || loadingProduct}
                  >
                    <FiCheck />
                    طلب مقاس خاص
                  </button>
                </div>
              )}
            </div>
          )}

          {step === STEPS.CONFIRM && (
            <div className={styles.card}>
              <p className={styles.stepTitle}>4 - تأكيد الطلب</p>

              <div className={styles.summaryRow}>
                <img src={mediaUrl(activeArImage)} alt={selectedProduct?.name} className={styles.summaryImg} />
                <div>
                  <strong>{selectedProduct?.name}</strong>
                  <p>{sizeCm.width} × {sizeCm.height} سم</p>
                </div>
              </div>

              <div className={styles.field}>
                <label>الخامة</label>
                <div className={styles.materials}>
                  <button
                    type="button"
                    className={material === "matte" ? `${styles.materialCard} ${styles.selected}` : styles.materialCard}
                    onClick={() => setMaterial("matte")}
                  >
                    Matte
                  </button>
                  <button
                    type="button"
                    className={material === "gloss" ? `${styles.materialCard} ${styles.selected}` : styles.materialCard}
                    onClick={() => setMaterial("gloss")}
                  >
                    Luster VIP
                  </button>
                </div>
              </div>

              {!user && <p className="warn">يجب تسجيل الدخول حتى تتمكن من إرسال الطلب</p>}

              <div className={styles.field}>
                <label>اسمك</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={30} className={styles.input} />
              </div>

              <div className={styles.field}>
                <label>رقم الهاتف</label>
                <input
                  type="tel"
                  dir="ltr"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  maxLength={20}
                  className={styles.input}
                />
              </div>

              <p className={styles.hint}>سيتم تسعير هذا الطلب من فريقنا بحسب المقاس الذى اخترته، ثم نتواصل معك لتأكيد الطلب</p>

              <div className={styles.stepActions}>
                <button type="button" className={styles.backBtn} onClick={() => setStep(STEPS.PLACE)}>
                  السابق
                </button>
                <button type="button" className={styles.primaryBtn} disabled={submitting} onClick={handleSubmit}>
                  {submitting ? <ImSpinner2 className="smallspinner" /> : <><FiCheck /> إرسال الطلب</>}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {maximized && (
        <div
          className={styles.maxBackdrop}
          onClick={(e) => {
            if (e.target === e.currentTarget) setMaximized(false);
          }}
        >
          <div className={styles.wallContainerMax}>
            {step === STEPS.OUTLINE ? outlineScene : wallScene}

            <button type="button" className={styles.maximizeBtn} aria-label="تصغير" onClick={() => setMaximized(false)}>
              <FiMinimize2 />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WallPreview;
